import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Divider,
  Paper,
  CircularProgress,
  TextField,
  Checkbox,
  FormGroup,
  Slider,
  Stack,
  Chip,
  MenuItem,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  VideoLibrary as VideoIcon,
  Group as GroupIcon,
  Restore as RestoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Stars as StarsIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface AppConfig {
  _id?: string;
  videoSwapEnabled: boolean;
  videoSwapPaused: boolean;
  groupFaceSwapEnabled: boolean;
  restoreImageEnabled: boolean;
  enhanceImageEnabled: boolean;
  creativeLooksEnabled: boolean;
  nsfwCheckEnabled: boolean;
  nsfwBlockedClasses?: string[];
  nsfwDefaultThreshold?: number;
  nsfwClassThresholds?: Record<string, number>;
  nsfwStoreImages?: boolean;
  csamCheckEnabled?: boolean;
  csamAgeThreshold?: number;
  csamAutoBlockEnabled?: boolean;
  freeCreativeLooksPerDevice?: number;
  welcomeCredits?: number;
  autoCleanupEnabled?: boolean;
  cleanupDelayMinutes?: number;
  videoCleanupDelayMinutes?: number;
  lastUpdatedBy?: {
    name: string;
    email: string;
  };
  updatedAt?: string;
}

// NudeNet classes that can be selected as NSFW blockers.
// Grouped by severity so admins can quickly toggle the most sensitive categories.
const NSFW_CLASS_GROUPS: { label: string; classes: string[] }[] = [
  {
    label: 'Explicit (recommended block list)',
    classes: [
      'FEMALE_GENITALIA_EXPOSED',
      'MALE_GENITALIA_EXPOSED',
      'FEMALE_BREAST_EXPOSED',
      'BUTTOCKS_EXPOSED',
      'ANUS_EXPOSED',
    ],
  },
  {
    label: 'Suggestive',
    classes: [
      'MALE_BREAST_EXPOSED',
      'BELLY_EXPOSED',
      'ARMPITS_EXPOSED',
      'FEET_EXPOSED',
    ],
  },
  {
    label: 'Covered (rarely blocked)',
    classes: [
      'FEMALE_GENITALIA_COVERED',
      'FEMALE_BREAST_COVERED',
      'BUTTOCKS_COVERED',
      'ANUS_COVERED',
      'BELLY_COVERED',
      'ARMPITS_COVERED',
      'FEET_COVERED',
    ],
  },
  {
    label: 'Face (informational, do not block)',
    classes: ['FACE_FEMALE', 'FACE_MALE'],
  },
];

const DEFAULT_BLOCKED_CLASSES = [
  'BUTTOCKS_EXPOSED',
  'FEMALE_BREAST_EXPOSED',
  'FEMALE_GENITALIA_EXPOSED',
  'MALE_GENITALIA_EXPOSED',
  'ANUS_EXPOSED',
];

// Device-wise (per-platform) Google Vision rules — nsfwPlatformVision on the server.
type NsfwPlatform = 'ios' | 'android';
interface PlatformVisionRules {
  forceVision: boolean;
  honorBypass: boolean;
  scanResult: boolean;
}
const NSFW_PLATFORMS: NsfwPlatform[] = ['ios', 'android'];
const PLATFORM_LABELS: Record<NsfwPlatform, string> = { ios: 'iOS', android: 'Android' };

// Same defaults as the server schema: nothing forced, bypass honoured, no result scan.
const normalizePlatformRules = (raw: any): PlatformVisionRules => ({
  forceVision: raw?.forceVision === true,
  honorBypass: raw?.honorBypass !== false,
  scanResult: raw?.scanResult === true,
});

const AppConfiguration: React.FC = () => {
  const [formData, setFormData] = useState<AppConfig>({
    videoSwapEnabled: true,
    videoSwapPaused: false,
    groupFaceSwapEnabled: true,
    restoreImageEnabled: true,
    enhanceImageEnabled: true,
    creativeLooksEnabled: true,
    nsfwCheckEnabled: false,
    freeCreativeLooksPerDevice: 3,
    welcomeCredits: 10,
    autoCleanupEnabled: true,
    cleanupDelayMinutes: 5,
    videoCleanupDelayMinutes: 3,
  });
  const [cleanupFormData, setCleanupFormData] = useState({
    autoCleanupEnabled: true,
    cleanupDelayMinutes: 5,
    videoCleanupDelayMinutes: 3,
  });
  const [freeCreativeLooks, setFreeCreativeLooks] = useState<number>(3);
  const [welcomeCredits, setWelcomeCredits] = useState<number>(10);
  const [nsfwBlockedClasses, setNsfwBlockedClasses] = useState<string[]>(DEFAULT_BLOCKED_CLASSES);
  const [nsfwDefaultThreshold, setNsfwDefaultThreshold] = useState<number>(0.5);
  const [nsfwClassThresholds, setNsfwClassThresholds] = useState<Record<string, number>>({});
  const [nsfwStoreImages, setNsfwStoreImages] = useState<boolean>(false);
  const [csamCheckEnabled, setCsamCheckEnabled] = useState<boolean>(true);
  const [csamAgeThreshold, setCsamAgeThreshold] = useState<number>(18);
  const [csamAutoBlockEnabled, setCsamAutoBlockEnabled] = useState<boolean>(true);
  // Google Vision NSFW (used for reviewer / test devices). The API key is never
  // returned by the server; visionApiKeyConfigured says whether one is stored,
  // and an empty visionApiKey input means "leave it unchanged".
  const [visionEnabled, setVisionEnabled] = useState<boolean>(false);
  const [visionApiKey, setVisionApiKey] = useState<string>('');
  const [visionApiKeyConfigured, setVisionApiKeyConfigured] = useState<boolean>(false);
  const [visionBlockOnError, setVisionBlockOnError] = useState<boolean>(false);
  const [visionVideoFrameCount, setVisionVideoFrameCount] = useState<number>(5);
  const [visionCategories, setVisionCategories] = useState<Record<string, string>>({
    adult: 'likely', racy: 'likely', violence: 'very_likely', medical: 'disabled', spoof: 'disabled'
  });
  // Which detector moderates ordinary (non-reviewer) traffic.
  const [nsfwEngine, setNsfwEngine] = useState<'nudenet' | 'vision'>('nudenet');
  const [platformVision, setPlatformVision] = useState<Record<NsfwPlatform, PlatformVisionRules>>({
    ios: normalizePlatformRules(null),
    android: normalizePlatformRules(null),
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const queryClient = useQueryClient();

  // Fetch app config
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['appConfig'],
    queryFn: async () => {
      const response = await apiService.getAppConfig();
      return response.data as AppConfig;
    }
  });

  // Update form data when data is fetched
  useEffect(() => {
    if (data) {
      setFormData(data);
      setFreeCreativeLooks(data.freeCreativeLooksPerDevice ?? 3);
      setWelcomeCredits(data.welcomeCredits ?? 10);
      setCleanupFormData({
        autoCleanupEnabled: data.autoCleanupEnabled ?? true,
        cleanupDelayMinutes: data.cleanupDelayMinutes ?? 5,
        videoCleanupDelayMinutes: data.videoCleanupDelayMinutes ?? 3,
      });
      setNsfwBlockedClasses(
        Array.isArray(data.nsfwBlockedClasses) && data.nsfwBlockedClasses.length > 0
          ? data.nsfwBlockedClasses
          : DEFAULT_BLOCKED_CLASSES
      );
      setNsfwDefaultThreshold(
        typeof data.nsfwDefaultThreshold === 'number' ? data.nsfwDefaultThreshold : 0.5
      );
      setNsfwClassThresholds(data.nsfwClassThresholds ?? {});
      setNsfwStoreImages(!!data.nsfwStoreImages);
      setCsamCheckEnabled(data.csamCheckEnabled !== false);
      setCsamAgeThreshold(
        typeof data.csamAgeThreshold === 'number' ? data.csamAgeThreshold : 18
      );
      setCsamAutoBlockEnabled(data.csamAutoBlockEnabled !== false);

      const v = (data as any).visionNsfwCheck || {};
      setVisionEnabled(!!v.enabled);
      setVisionApiKeyConfigured(!!v.apiKeyConfigured);
      setVisionApiKey('');
      setVisionBlockOnError(!!v.blockOnError);
      setVisionVideoFrameCount(typeof v.videoFrameCount === 'number' ? v.videoFrameCount : 5);
      if (v.categories && typeof v.categories === 'object') {
        setVisionCategories((prev) => ({ ...prev, ...v.categories }));
      }
      setNsfwEngine((data as any).nsfwEngine === 'vision' ? 'vision' : 'nudenet');

      const pv = (data as any).nsfwPlatformVision || {};
      setPlatformVision({
        ios: normalizePlatformRules(pv.ios),
        android: normalizePlatformRules(pv.android),
      });
    }
  }, [data]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (config: AppConfig) => {
      const response = await apiService.updateAppConfig(config);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: 'App configuration updated successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update app configuration',
        severity: 'error'
      });
    }
  });

  // Toggle video swap mutation
  const toggleVideoSwapMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleVideoSwap(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Video swap ${formData.videoSwapEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle video swap',
        severity: 'error'
      });
    }
  });

  // Toggle group face swap mutation
  const toggleGroupFaceSwapMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleGroupFaceSwap(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Group face swap ${formData.groupFaceSwapEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle group face swap',
        severity: 'error'
      });
    }
  });

  // Toggle restore image mutation
  const toggleRestoreImageMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleRestoreImage(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Restore image ${formData.restoreImageEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle restore image',
        severity: 'error'
      });
    }
  });

  // Toggle enhance image mutation
  const toggleEnhanceImageMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleEnhanceImage(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Enhance image ${formData.enhanceImageEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle enhance image',
        severity: 'error'
      });
    }
  });

  // Toggle creative looks mutation
  const toggleCreativeLooksMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleCreativeLooks(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Creative looks ${formData.creativeLooksEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle creative looks',
        severity: 'error'
      });
    }
  });

  // Toggle NSFW check mutation
  const toggleNsfwCheckMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiService.toggleNsfwCheck(enabled);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `NSFW check ${formData.nsfwCheckEnabled ? 'disabled' : 'enabled'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle NSFW check',
        severity: 'error'
      });
    }
  });

  // Update NSFW detection settings (blocked classes + thresholds + storage + CSAM)
  const updateNsfwSettingsMutation = useMutation({
    mutationFn: async (settings: {
      nsfwBlockedClasses?: string[];
      nsfwDefaultThreshold?: number;
      nsfwClassThresholds?: Record<string, number>;
      nsfwStoreImages?: boolean;
      csamCheckEnabled?: boolean;
      csamAgeThreshold?: number;
      csamAutoBlockEnabled?: boolean;
    }) => {
      const response = await apiService.updateNsfwSettings(settings);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: 'NSFW detection settings saved successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save NSFW detection settings',
        severity: 'error'
      });
    }
  });

  // Engine availability (which engines can run + which is active/forced).
  const { data: engineHealth } = useQuery({
    queryKey: ['nsfwEngineHealth'],
    queryFn: async () => {
      const r = await apiService.getNsfwEngineHealth();
      return r.data as {
        active: 'nudenet' | 'vision';
        forcedScanEngine: 'nudenet' | 'vision' | 'none';
        nudenet: { available: boolean; reason?: string };
        vision: { available: boolean; reason?: string };
      };
    }
  });

  // Switch the engine that moderates ordinary traffic. Saved immediately on
  // change (via the NSFW settings endpoint), like the reference panel.
  const updateNsfwEngineMutation = useMutation({
    mutationFn: async (engine: 'nudenet' | 'vision') => {
      const response = await apiService.updateNsfwSettings({ nsfwEngine: engine } as any);
      return response.data;
    },
    onSuccess: (_res, engine) => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      queryClient.invalidateQueries({ queryKey: ['nsfwEngineHealth'] });
      setSnackbar({
        open: true,
        message: `Ordinary-traffic detector set to ${engine === 'vision' ? 'Google Vision' : 'NudeNet'}`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setNsfwEngine((data as any)?.nsfwEngine === 'vision' ? 'vision' : 'nudenet');
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to switch detector',
        severity: 'error'
      });
    }
  });

  const handleNsfwEngineChange = (engine: 'nudenet' | 'vision') => {
    setNsfwEngine(engine);
    updateNsfwEngineMutation.mutate(engine);
  };

  const updateVisionNsfwMutation = useMutation({
    mutationFn: async () => {
      const response = await apiService.updateVisionNsfwSettings({
        enabled: visionEnabled,
        // Empty means "keep existing key".
        apiKey: visionApiKey.trim() ? visionApiKey.trim() : undefined,
        blockOnError: visionBlockOnError,
        videoFrameCount: visionVideoFrameCount,
        categories: visionCategories
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setVisionApiKey('');
      setSnackbar({ open: true, message: 'Vision NSFW settings saved successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save Vision NSFW settings',
        severity: 'error'
      });
    }
  });

  const updatePlatformVisionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiService.updatePlatformVisionSettings({ platforms: platformVision });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({ open: true, message: 'Device-wise Vision settings saved successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save device-wise Vision settings',
        severity: 'error'
      });
    }
  });

  const setPlatformRule = (platform: NsfwPlatform, patch: Partial<PlatformVisionRules>) => {
    setPlatformVision((prev) => ({ ...prev, [platform]: { ...prev[platform], ...patch } }));
  };

  const handleToggleBlockedClass = (cls: string) => {
    setNsfwBlockedClasses(prev =>
      prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]
    );
  };

  const handleClassThresholdChange = (cls: string, value: number | null) => {
    setNsfwClassThresholds(prev => {
      const next = { ...prev };
      if (value === null || Number.isNaN(value)) {
        delete next[cls];
      } else {
        next[cls] = Math.min(Math.max(value, 0), 1);
      }
      return next;
    });
  };

  const handleSaveNsfwSettings = () => {
    updateNsfwSettingsMutation.mutate({
      nsfwBlockedClasses,
      nsfwDefaultThreshold,
      nsfwClassThresholds,
      nsfwStoreImages,
      csamCheckEnabled,
      csamAgeThreshold,
      csamAutoBlockEnabled,
    });
  };

  // Toggle video swap paused mutation
  const toggleVideoSwapPausedMutation = useMutation({
    mutationFn: async (paused: boolean) => {
      const response = await apiService.toggleVideoSwapPaused(paused);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Video swap ${formData.videoSwapPaused ? 'resumed' : 'paused'} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to toggle video swap paused status',
        severity: 'error'
      });
    }
  });

  const handleToggleVideoSwap = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setFormData(prev => ({ ...prev, videoSwapEnabled: enabled }));
    toggleVideoSwapMutation.mutate(enabled);
  };

  const handleToggleGroupFaceSwap = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setFormData(prev => ({ ...prev, groupFaceSwapEnabled: enabled }));
    toggleGroupFaceSwapMutation.mutate(enabled);
  };

  const handleToggleRestoreImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setFormData(prev => ({ ...prev, restoreImageEnabled: enabled }));
    toggleRestoreImageMutation.mutate(enabled);
  };

  const handleToggleEnhanceImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setFormData(prev => ({ ...prev, enhanceImageEnabled: enabled }));
    toggleEnhanceImageMutation.mutate(enabled);
  };

  const handleToggleCreativeLooks = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setFormData(prev => ({ ...prev, creativeLooksEnabled: enabled }));
    toggleCreativeLooksMutation.mutate(enabled);
  };

  const handleToggleNsfwCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setFormData(prev => ({ ...prev, nsfwCheckEnabled: enabled }));
    toggleNsfwCheckMutation.mutate(enabled);
  };

  const handleToggleVideoSwapPaused = (event: React.ChangeEvent<HTMLInputElement>) => {
    const paused = event.target.checked;
    setFormData(prev => ({ ...prev, videoSwapPaused: paused }));
    toggleVideoSwapPausedMutation.mutate(paused);
  };

  // Update cleanup settings mutation
  const updateCleanupSettingsMutation = useMutation({
    mutationFn: async (settings: { autoCleanupEnabled?: boolean; cleanupDelayMinutes?: number; videoCleanupDelayMinutes?: number }) => {
      const response = await apiService.updateCleanupSettings(settings);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: 'Cleanup settings updated successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update cleanup settings',
        severity: 'error'
      });
    }
  });

  const handleCleanupSettingsChange = (field: string, value: boolean | number) => {
    setCleanupFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCleanupSettings = () => {
    updateCleanupSettingsMutation.mutate(cleanupFormData);
  };

  // Update welcome credits mutation
  const updateWelcomeCreditsMutation = useMutation({
    mutationFn: async (credits: number) => {
      const response = await apiService.updateWelcomeCredits(credits);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appConfig'] });
      setSnackbar({
        open: true,
        message: `Welcome credits updated to ${welcomeCredits} successfully`,
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update welcome credits',
        severity: 'error'
      });
    }
  });

  const handleWelcomeCreditsChange = (value: number) => {
    if (value >= 0 && value <= 100) {
      setWelcomeCredits(value);
    }
  };

  const handleSaveWelcomeCredits = () => {
    updateWelcomeCreditsMutation.mutate(welcomeCredits);
  };

  const handleSaveFreeCreativeLooks = () => {
    updateMutation.mutate({ ...formData, freeCreativeLooksPerDevice: freeCreativeLooks });
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load app configuration. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        App Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure app features and visibility settings.
      </Typography>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {/* Video Swap Feature */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <VideoIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Video Swap Feature
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show or hide the video swap option in the app's bottom navigation menu
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.videoSwapEnabled}
                        onChange={handleToggleVideoSwap}
                        disabled={toggleVideoSwapMutation.isPending}
                        color="primary"
                      />
                    }
                    label={formData.videoSwapEnabled ? 'Visible' : 'Hidden'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Video Swap Paused */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <VideoIcon color="warning" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Video Swap Temporarily Paused
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        When enabled, shows an overlay message indicating video swap is temporarily paused for upgrade. Users can still access the screen but cannot perform swaps.
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.videoSwapPaused}
                        onChange={handleToggleVideoSwapPaused}
                        disabled={toggleVideoSwapPausedMutation.isPending}
                        color="warning"
                      />
                    }
                    label={formData.videoSwapPaused ? 'Paused' : 'Active'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Group Face Swap Feature */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <GroupIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Group Face Swap Feature
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show or hide the group face swap option in the app's bottom navigation menu
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.groupFaceSwapEnabled}
                        onChange={handleToggleGroupFaceSwap}
                        disabled={toggleGroupFaceSwapMutation.isPending}
                        color="primary"
                      />
                    }
                    label={formData.groupFaceSwapEnabled ? 'Visible' : 'Hidden'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Restore Image Feature */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <RestoreIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Restore Old Images Feature
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show or hide the restore old images option in the app's home grid
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.restoreImageEnabled}
                        onChange={handleToggleRestoreImage}
                        disabled={toggleRestoreImageMutation.isPending}
                        color="primary"
                      />
                    }
                    label={formData.restoreImageEnabled ? 'Visible' : 'Hidden'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Enhance Image Feature */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <AutoAwesomeIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Enhance Image Feature
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show or hide the enhance image option in the app's home grid
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enhanceImageEnabled}
                        onChange={handleToggleEnhanceImage}
                        disabled={toggleEnhanceImageMutation.isPending}
                        color="primary"
                      />
                    }
                    label={formData.enhanceImageEnabled ? 'Visible' : 'Hidden'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Creative Looks Feature */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <AutoAwesomeIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Creative Looks AI Feature
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Show or hide the creative looks AI option in the app's home grid
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.creativeLooksEnabled}
                        onChange={handleToggleCreativeLooks}
                        disabled={toggleCreativeLooksMutation.isPending}
                        color="primary"
                      />
                    }
                    label={formData.creativeLooksEnabled ? 'Visible' : 'Hidden'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* NSFW Content Check */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <SecurityIcon color={formData.nsfwCheckEnabled ? 'success' : 'disabled'} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        NSFW Content Check (NudeNet)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Detects inappropriate body-part exposure in uploaded images and blocks them before processing.
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.nsfwCheckEnabled}
                        onChange={handleToggleNsfwCheck}
                        disabled={toggleNsfwCheckMutation.isPending}
                        color="primary"
                      />
                    }
                    label={formData.nsfwCheckEnabled ? 'Enabled' : 'Disabled'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* NSFW Detection Configuration */}
            {formData.nsfwCheckEnabled && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default' }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        NSFW Detection Configuration
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select which body-part categories should trigger an NSFW block,
                        and tune the confidence threshold. NudeNet returns a confidence
                        score (0–1) per detected category; an image is blocked when any
                        selected category scores above the threshold.
                      </Typography>
                    </Box>

                    {/* Default threshold */}
                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Default confidence threshold: <Chip size="small" label={nsfwDefaultThreshold.toFixed(2)} />
                      </Typography>
                      <Slider
                        value={nsfwDefaultThreshold}
                        onChange={(_, v) => setNsfwDefaultThreshold(Array.isArray(v) ? v[0] : v)}
                        min={0}
                        max={1}
                        step={0.05}
                        marks={[
                          { value: 0, label: '0' },
                          { value: 0.5, label: '0.5' },
                          { value: 1, label: '1' }
                        ]}
                        valueLabelDisplay="auto"
                        disabled={updateNsfwSettingsMutation.isPending}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Lower = stricter (more false positives). Higher = more lenient. Recommended: 0.4–0.6.
                      </Typography>
                    </Box>

                    <Divider />

                    {/* Blocked classes (grouped) */}
                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Blocked categories
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Uncheck a category to allow it. Optional per-category threshold overrides
                        the default above.
                      </Typography>
                      {NSFW_CLASS_GROUPS.map(group => (
                        <Box key={group.label} sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                            {group.label}
                          </Typography>
                          <FormGroup>
                            <Grid container spacing={1}>
                              {group.classes.map(cls => {
                                const checked = nsfwBlockedClasses.includes(cls);
                                const override = nsfwClassThresholds[cls];
                                return (
                                  <Grid item xs={12} sm={6} md={4} key={cls}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <FormControlLabel
                                        sx={{ flex: 1, mr: 0 }}
                                        control={
                                          <Checkbox
                                            checked={checked}
                                            onChange={() => handleToggleBlockedClass(cls)}
                                            size="small"
                                          />
                                        }
                                        label={
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {cls}
                                          </Typography>
                                        }
                                      />
                                      <TextField
                                        size="small"
                                        type="number"
                                        placeholder="default"
                                        value={override ?? ''}
                                        onChange={(e) => {
                                          const v = e.target.value === '' ? null : parseFloat(e.target.value);
                                          handleClassThresholdChange(cls, v);
                                        }}
                                        inputProps={{ min: 0, max: 1, step: 0.05, style: { width: 60, padding: '4px 6px' } }}
                                        disabled={!checked}
                                        sx={{ width: 80 }}
                                      />
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </FormGroup>
                        </Box>
                      ))}
                    </Box>

                    <Divider />

                    {/* Image storage toggle */}
                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Store blocked images for admin review
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={nsfwStoreImages}
                            onChange={(e) => setNsfwStoreImages(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={nsfwStoreImages ? 'Storing images' : 'Not storing images (metadata only)'}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        When enabled, every image blocked by the NSFW detector is copied to
                        <code style={{ marginLeft: 4, marginRight: 4 }}>server/nsfw_incidents/</code>
                        and viewable in the NSFW Monitoring page. Disable this to keep only the
                        detection metadata (matched class + score) without retaining user content.
                      </Typography>
                    </Box>

                    <Divider />

                    {/* Child Safety (CSAM) settings */}
                    <Box sx={{
                      p: 2,
                      bgcolor: 'rgba(244, 67, 54, 0.06)',
                      border: '1px solid rgba(244, 67, 54, 0.25)',
                      borderRadius: 1
                    }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="error" gutterBottom>
                        Child Safety (CSAM Detection)
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        When NudeNet flags an image AND any detected face is estimated below
                        the age threshold, the incident is escalated as CSAM-suspected. Age
                        is estimated using the InsightFace <code>genderage.onnx</code> model
                        already loaded by the face-swap pipeline (Apache 2.0). The
                        accompanying device + signed-in user are auto-permabanned and the
                        incident is flagged in the moderation queue with severity = csam.
                      </Typography>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={csamCheckEnabled}
                            onChange={(e) => setCsamCheckEnabled(e.target.checked)}
                            color="error"
                          />
                        }
                        label={csamCheckEnabled ? 'Child-safety escalation enabled' : 'Child-safety escalation disabled'}
                      />

                      {csamCheckEnabled && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" fontWeight="medium" gutterBottom>
                            Minor age threshold: <Chip size="small" label={`< ${csamAgeThreshold} years`} color="error" />
                          </Typography>
                          <Slider
                            value={csamAgeThreshold}
                            onChange={(_, v) => setCsamAgeThreshold(Array.isArray(v) ? v[0] : v)}
                            min={10}
                            max={25}
                            step={1}
                            marks={[
                              { value: 13, label: '13' },
                              { value: 18, label: '18' },
                              { value: 21, label: '21' }
                            ]}
                            valueLabelDisplay="auto"
                            disabled={updateNsfwSettingsMutation.isPending}
                            sx={{ maxWidth: 480 }}
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Recommended: 18. Higher values catch more potential CSAM at the cost of more false positives on young-looking adults.
                          </Typography>

                          <FormControlLabel
                            sx={{ mt: 2, display: 'flex' }}
                            control={
                              <Switch
                                checked={csamAutoBlockEnabled}
                                onChange={(e) => setCsamAutoBlockEnabled(e.target.checked)}
                                color="error"
                              />
                            }
                            label={
                              csamAutoBlockEnabled
                                ? 'Auto-permaban offending device + account on CSAM detection'
                                : 'Log CSAM incident only (no auto-block)'
                            }
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            When auto-block is off, CSAM incidents are still flagged in the
                            moderation queue but require manual admin action.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveNsfwSettings}
                        disabled={updateNsfwSettingsMutation.isPending}
                      >
                        Save NSFW Settings
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            )}

            {/* NSFW detection engine — which model moderates ordinary traffic */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  NSFW Detection Engine
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Google testers, emulators and datacenter IPs are <strong>always</strong> scanned with
                  Google Vision — regardless of this setting and regardless of any per-device NSFW
                  bypass. Platforms forced under "Device-wise Force Vision" below are scanned with Vision
                  too. This selection only applies to the remaining ordinary user traffic.
                  {engineHealth && (
                    <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 'medium' }}>
                      Forced scan engine for reviewer/test traffic: {engineHealth.forcedScanEngine}
                    </Box>
                  )}
                </Alert>

                {engineHealth?.forcedScanEngine === 'none' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No forced scan engine is available — reviewer, emulator and datacenter traffic is
                    currently unmoderated.
                  </Alert>
                )}

                <FormControl disabled={updateNsfwEngineMutation.isPending}>
                  <FormLabel sx={{ mb: 1 }}>Active engine for ordinary traffic</FormLabel>
                  <RadioGroup
                    value={nsfwEngine}
                    onChange={(e) => handleNsfwEngineChange(e.target.value as 'nudenet' | 'vision')}
                  >
                    {([
                      { value: 'nudenet', label: 'NudeNet (self-hosted)', help: 'Free, runs on the Python service. Configured in the NSFW detection settings above.' },
                      { value: 'vision', label: 'Google Vision SafeSearch', help: 'Hosted, per-image cost, needs an API key. Configured below.' }
                    ] as const).map((option) => {
                      const health = engineHealth?.[option.value];
                      const available = health?.available ?? true;
                      return (
                        <Box key={option.value} sx={{ mb: 1.5 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FormControlLabel
                              value={option.value}
                              control={<Radio />}
                              label={option.label}
                              disabled={updateNsfwEngineMutation.isPending || (!!health && !available)}
                              sx={{ mr: 0 }}
                            />
                            {health && (
                              <Chip size="small" color={available ? 'success' : 'error'} label={available ? 'Available' : 'Unavailable'} />
                            )}
                            {engineHealth?.active === option.value && (
                              <Chip size="small" variant="outlined" label="Active" />
                            )}
                          </Box>
                          <FormHelperText sx={{ ml: 4, mt: 0 }}>{option.help}</FormHelperText>
                          {health && !available && (
                            <FormHelperText error sx={{ ml: 4, mt: 0 }}>
                              {health.reason || 'This engine cannot run on the server right now.'}
                            </FormHelperText>
                          )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </Paper>
            </Grid>

            {/* Google Vision NSFW — used for reviewer / test devices */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Google Vision (reviewer / test devices)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ordinary traffic is moderated by NudeNet (above). When a request is flagged as a
                      reviewer / test device (Google-owned or datacenter IP, or an emulator) and Vision
                      is enabled with a key, that scan runs through Google Cloud Vision SafeSearch
                      instead — always on, regardless of the global NudeNet toggle or any bypass.
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={visionEnabled}
                        onChange={(e) => setVisionEnabled(e.target.checked)}
                      />
                    }
                    label={visionEnabled ? 'Vision enabled for reviewer devices' : 'Vision disabled'}
                  />

                  <TextField
                    fullWidth
                    type="password"
                    label="Google Cloud Vision API key"
                    name="vision-api-key-field"
                    value={visionApiKey}
                    onChange={(e) => setVisionApiKey(e.target.value)}
                    placeholder={visionApiKeyConfigured ? '•••••••• (a key is configured — leave blank to keep it)' : 'Paste your Vision API key'}
                    // Stop browser / password-manager autofill from silently
                    // injecting a saved credential — that value would otherwise
                    // overwrite the stored Vision key on the next save.
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: 'new-password',
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                    helperText={
                      visionApiKeyConfigured
                        ? 'A key is stored. Leave this blank to keep it; type a new key to replace it.'
                        : 'Required for Vision to run. The key is stored server-side and never shown again.'
                    }
                  />

                  <Grid container spacing={2}>
                    {(['adult', 'racy', 'violence', 'medical', 'spoof'] as const).map((cat) => (
                      <Grid item xs={12} sm={6} md={4} key={cat}>
                        <TextField
                          select
                          fullWidth
                          label={`Block "${cat}" at`}
                          value={visionCategories[cat] || 'disabled'}
                          onChange={(e) => setVisionCategories((prev) => ({ ...prev, [cat]: e.target.value }))}
                        >
                          {['disabled', 'very_unlikely', 'unlikely', 'possible', 'likely', 'very_likely'].map((lvl) => (
                            <MenuItem key={lvl} value={lvl}>{lvl.replace('_', ' ')}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography variant="caption" color="text.secondary">
                    An image is blocked when Vision's returned likelihood for a category is at or above
                    the level you set. "disabled" never blocks on that category.
                  </Typography>

                  <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <TextField
                      type="number"
                      label="Video frames sampled"
                      value={visionVideoFrameCount}
                      onChange={(e) => setVisionVideoFrameCount(Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 5)))}
                      inputProps={{ min: 1, max: 10 }}
                      sx={{ width: 200 }}
                      helperText="1–10. Each frame is a separate Vision call."
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={visionBlockOnError}
                          onChange={(e) => setVisionBlockOnError(e.target.checked)}
                        />
                      }
                      label={visionBlockOnError ? 'Block on Vision error (fail-closed)' : 'Allow on Vision error (fail-open)'}
                    />
                  </Box>

                  {visionEnabled && !visionApiKeyConfigured && !visionApiKey.trim() && (
                    <Alert severity="warning">
                      Vision is enabled but no API key is configured — reviewer devices will fall back
                      to NudeNet until a key is saved.
                    </Alert>
                  )}

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={() => updateVisionNsfwMutation.mutate()}
                      disabled={updateVisionNsfwMutation.isPending}
                    >
                      Save Vision Settings
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Device-wise Force Vision — per-platform rules (nsfwPlatformVision) */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Device-wise Force Vision
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Force a Google Vision scan on every device of a platform, even when the global NSFW
                      check is off. Vision uses the category block levels set in the Google Vision
                      section above.
                    </Typography>
                  </Box>

                  {!visionApiKeyConfigured && NSFW_PLATFORMS.some((p) => platformVision[p].forceVision || platformVision[p].scanResult) && (
                    <Alert severity="warning">
                      No Vision API key is saved. Forced platforms are scanned with NudeNet instead, and
                      generated results are not scanned until a key is saved.
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    {NSFW_PLATFORMS.map((p) => {
                      const rules = platformVision[p];
                      const label = PLATFORM_LABELS[p];
                      return (
                        <Grid item xs={12} md={6} key={p}>
                          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                            <Stack spacing={1}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
                                {rules.forceVision && <Chip size="small" color="warning" label="Forced" />}
                                {rules.scanResult && <Chip size="small" variant="outlined" label="Result scan" />}
                              </Box>

                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={rules.forceVision}
                                    onChange={(e) => setPlatformRule(p, { forceVision: e.target.checked })}
                                  />
                                }
                                label={`Force Vision scan on ${label}`}
                              />

                              <Box>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={rules.honorBypass}
                                      disabled={!rules.forceVision}
                                      onChange={(e) => setPlatformRule(p, { honorBypass: e.target.checked })}
                                    />
                                  }
                                  label="Skip devices / accounts that already have an NSFW bypass"
                                />
                                <FormHelperText sx={{ mt: 0, ml: 6 }}>
                                  {rules.honorBypass
                                    ? 'Bypassed devices and accounts stay unscanned.'
                                    : 'The force overrides existing bypasses — bypassed devices and accounts are scanned too.'}
                                </FormHelperText>
                              </Box>

                              <Box>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={rules.scanResult}
                                      onChange={(e) => setPlatformRule(p, { scanResult: e.target.checked })}
                                    />
                                  }
                                  label="Scan generated result (image & video)"
                                />
                                <FormHelperText sx={{ mt: 0, ml: 6 }}>
                                  Runs Vision on the swap output before it is returned (extra Vision calls per
                                  swap; videos use the frame count above). Only for requests that are scanned at
                                  all, so a skipped bypass skips this too.
                                </FormHelperText>
                              </Box>
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={() => updatePlatformVisionMutation.mutate()}
                      disabled={updatePlatformVisionMutation.isPending}
                    >
                      Save Device-wise Settings
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Free Creative Looks Per Device */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Free Creative Looks Per Device"
                      type="number"
                      value={freeCreativeLooks}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value)) {
                          setFreeCreativeLooks(Math.min(Math.max(value, 0), 50));
                        }
                      }}
                      inputProps={{ min: 0, max: 50 }}
                      helperText="Free creative look generations per device (0-50). Set to 0 to disable free usage."
                      disabled={updateMutation.isPending}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveFreeCreativeLooks}
                      disabled={updateMutation.isPending}
                      fullWidth
                    >
                      Save Free Creative Looks
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Welcome Credits Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
                <StarsIcon color="primary" />
                <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                  Welcome Credits Configuration
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure credits given to new devices on first registration. Only given once per device.
              </Typography>
            </Grid>

            {/* Welcome Credits Input */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Welcome Credits"
                      type="number"
                      value={welcomeCredits}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value)) {
                          handleWelcomeCreditsChange(value);
                        }
                      }}
                      inputProps={{ min: 0, max: 100 }}
                      helperText="Credits given to new devices on first registration (0-100)"
                      disabled={updateWelcomeCreditsMutation.isPending}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveWelcomeCredits}
                      disabled={updateWelcomeCreditsMutation.isPending}
                      fullWidth
                    >
                      Save Welcome Credits
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Cleanup Settings Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                File Cleanup Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure automatic cleanup of generated result images and videos.
              </Typography>
            </Grid>

            {/* Auto Cleanup Toggle */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <DeleteIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Auto Cleanup
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically delete result images and videos after a specified time
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={cleanupFormData.autoCleanupEnabled}
                        onChange={(e) => handleCleanupSettingsChange('autoCleanupEnabled', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={cleanupFormData.autoCleanupEnabled ? 'Enabled' : 'Disabled'}
                    labelPlacement="end"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Cleanup Delay Settings */}
            {cleanupFormData.autoCleanupEnabled && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Image Cleanup Delay (minutes)"
                    type="number"
                    value={cleanupFormData.cleanupDelayMinutes}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= 1 && value <= 1440) {
                        handleCleanupSettingsChange('cleanupDelayMinutes', value);
                      }
                    }}
                    inputProps={{ min: 1, max: 1440 }}
                    helperText="Time in minutes before result images are deleted (1-1440 minutes)"
                    disabled={updateCleanupSettingsMutation.isPending}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Video Cleanup Delay (minutes)"
                    type="number"
                    value={cleanupFormData.videoCleanupDelayMinutes}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (!isNaN(value) && value >= 1 && value <= 1440) {
                        handleCleanupSettingsChange('videoCleanupDelayMinutes', value);
                      }
                    }}
                    inputProps={{ min: 1, max: 1440 }}
                    helperText="Time in minutes before result videos are deleted (1-1440 minutes)"
                    disabled={updateCleanupSettingsMutation.isPending}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveCleanupSettings}
                      disabled={updateCleanupSettingsMutation.isPending}
                    >
                      Save Cleanup Settings
                    </Button>
                  </Box>
                </Grid>
              </>
            )}

            {/* Last Updated Info */}
            {data?.updatedAt && (
              <Grid item xs={12}>
                <Divider />
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {new Date(data.updatedAt).toLocaleString()}
                    {data.lastUpdatedBy && (
                      <> by {data.lastUpdatedBy.name} ({data.lastUpdatedBy.email})</>
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  disabled={isLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={updateMutation.isPending || toggleVideoSwapMutation.isPending || toggleGroupFaceSwapMutation.isPending}
                >
                  Save Changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppConfiguration;

